import { TUTORIAL_STEPS } from '../data/tutorialSteps';

// Returns the current tutorial step object, or null if tutorial complete/disabled
export function getCurrentTutorialStep(state) {
  if (!state.tutorial?.enabled) return null;
  const step = state.tutorial.currentStep;
  if (step === null || step >= TUTORIAL_STEPS.length) return null;
  return TUTORIAL_STEPS[step];
}

// Called from reducer after each action to check if we should advance the tutorial.
// Returns the next step index if the action completes the current step, otherwise null.
export function checkTutorialCompletion(tutorial, actionType, payload, gameDay) {
  if (!tutorial?.enabled) return null;
  const currentStepIndex = tutorial.currentStep;
  if (currentStepIndex === null || currentStepIndex >= TUTORIAL_STEPS.length) return null;

  const step = TUTORIAL_STEPS[currentStepIndex];
  if (!step.completionCondition) return null;

  const condition = step.completionCondition;

  switch (condition) {
    case 'TUTORIAL_DISMISS_WELCOME':
      return actionType === 'TUTORIAL_DISMISS_WELCOME' ? currentStepIndex + 1 : null;

    case 'NAVIGATE_ROOM':
      return (actionType === 'NAVIGATE' && payload?.screen === 'room') ? currentStepIndex + 1 : null;

    case 'VIEWED_ROOM':
      // Advances when player navigates to room screen (same trigger, but step is "view env")
      return (actionType === 'NAVIGATE' && payload?.screen === 'room') ? currentStepIndex + 1 : null;

    case 'SET_RECIPE_TARGET':
      return actionType === 'SET_RECIPE_TARGET' ? currentStepIndex + 1 : null;

    case 'PLANT_BATCH':
      return actionType === 'PLANT_BATCH' ? currentStepIndex + 1 : null;

    case 'HIRE_WORKER':
      return actionType === 'HIRE_WORKER' ? currentStepIndex + 1 : null;

    case 'ASSIGN_WORKER':
      return actionType === 'ASSIGN_WORKER' ? currentStepIndex + 1 : null;

    case 'DAY_7':
      // Checked in TICK based on gameDay
      return (actionType === '_TIME_CHECK' && gameDay >= 7) ? currentStepIndex + 1 : null;

    case 'STAGE_FLOWERING':
      // Advances when a batch enters flowering — checked via TICK
      return actionType === '_BATCH_FLOWERING' ? currentStepIndex + 1 : null;

    case 'HARVEST_BATCH':
      return actionType === 'HARVEST_BATCH' ? currentStepIndex + 1 : null;

    case 'SELL_HARVEST':
      return actionType === 'SELL_HARVEST' ? currentStepIndex + 1 : null;

    default:
      return null;
  }
}

// Returns a contextual hint for the NextActionBar based on current game state.
// Priority: tutorial hint > urgent game hint > general hint > null (hide bar)
export function getNextActionHint(state) {
  // 1. Active tutorial step
  const tutStep = getCurrentTutorialStep(state);
  if (tutStep && tutStep.step > 0 && tutStep.action) {
    return {
      icon: '📖',
      text: `Tutorial: ${tutStep.action}`,
      screen: tutStep.screen,
      isTutorial: true,
    };
  }

  // 2. Critically low cash
  if (state.weeklyBurn > 0 && state.cash < state.weeklyBurn * 2) {
    const inventoryGrams = (state.inventory || []).filter(i => i.status !== 'sold').reduce((s, i) => s + i.grams, 0);
    if (inventoryGrams > 0) {
      return {
        icon: '⚠',
        text: `Low funds — sell your ${inventoryGrams}g of inventory soon`,
        screen: 'market',
      };
    }
    return {
      icon: '⚠',
      text: 'Low funds — harvest and sell as soon as possible',
      screen: 'facility',
    };
  }

  // 3. Harvest-ready batch
  const harvestRoom = (state.rooms || []).find(r => r.batch?.stage === 'harvest');
  if (harvestRoom) {
    return {
      icon: '✂️',
      text: `${harvestRoom.name} is ready to harvest`,
      screen: 'room',
      roomId: harvestRoom.id,
    };
  }

  // 4. Unresolved high-severity event
  const criticalEvent = (state.activeEvents || []).find(e => !e.resolved && e.severity === 'high');
  if (criticalEvent) {
    const eventRoom = (state.rooms || []).find(r => r.id === criticalEvent.roomId);
    return {
      icon: '🚨',
      text: `${criticalEvent.label || 'Event'} needs attention${eventRoom ? ` in ${eventRoom.name}` : ''}`,
      screen: 'facility',
    };
  }

  // 5. All rooms empty with seeds available
  const allEmpty = (state.rooms || []).filter(r => r.unlocked).every(r => !r.batch);
  const hasSeeds = Object.values(state.seedInventory || {}).some(count => count > 0);
  if (allEmpty && hasSeeds) {
    const emptyRoom = (state.rooms || []).find(r => r.unlocked && !r.batch);
    return {
      icon: '🪴',
      text: `Plant a new batch in ${emptyRoom?.name || 'your room'}`,
      screen: 'room',
      roomId: emptyRoom?.id,
    };
  }

  // 6. Unassigned worker
  const unassigned = (state.workers || []).find(w => !w.assignedRoomId);
  if (unassigned) {
    return {
      icon: '👷',
      text: `${unassigned.name} is unassigned — assign them to a room`,
      screen: 'workforce',
    };
  }

  // 7. Sellable inventory with no active contracts
  const sellable = (state.inventory || []).filter(i => i.status !== 'sold');
  if (sellable.length > 0 && (state.activeContracts || []).length === 0) {
    const totalG = sellable.reduce((s, i) => s + i.grams, 0);
    return {
      icon: '💵',
      text: `You have ${totalG}g ready to sell — go to Market`,
      screen: 'market',
    };
  }

  return null;
}
